import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { AgremoAnalysisTypeCodebook } from '../response-models/agremo-analysis-type-codebook.model';
import { AgremoCameraType } from '../response-models/agremo-camera-type.model';
import { AgremoHealthStatus } from '../response-models/agremo-health-status.model';
import { AgremoLayerStatusCode } from '../response-models/agremo-layer-status-code.model';
import { AgremoLayerType } from '../response-models/agremo-layer-type.model';
import { AgremoMapStatusCode } from '../response-models/agremo-map-status-code.model';
import { isRecord } from '../utils/is-record';
import { countOrZero, nullableBoolean, nullableString } from '../utils/wire-value-utils';

/**
 * Agremo connectivity and codebooks: `/admin/agremo/health` (hub `admin-agremo-health-controller.ts`).
 * The codebook routes answer 503 when Agremo isn't configured and 502 when the upstream call fails.
 */
@Injectable({ providedIn: 'root' })
export class AgremoHealthService extends ApiService {
  constructor() {
    super('admin/agremo/health');
  }

  /** GET /admin/agremo/health. A failed check is still a 200 with `connected: false`. */
  checkHealth(): Observable<AgremoHealthStatus> {
    return this.request('GET', {}, toHealthStatus);
  }

  /** GET /admin/agremo/health/map-statuses */
  getMapStatuses(): Observable<AgremoMapStatusCode[]> {
    return this.request('GET', { path: 'map-statuses' }, data => rows(data, toStatusCode));
  }

  /** GET /admin/agremo/health/cameras */
  getCameraTypes(): Observable<AgremoCameraType[]> {
    return this.request('GET', { path: 'cameras' }, data =>
      rows(data, row => ({ camera_code: countOrZero(row['camera_code']), camera_bands: nullableString(row['camera_bands']) ?? '' }))
    );
  }

  /** GET /admin/agremo/health/layer-types */
  getLayerTypes(): Observable<AgremoLayerType[]> {
    return this.request('GET', { path: 'layer-types' }, data =>
      rows(data, row => ({
        layer_type_code: countOrZero(row['layer_type_code']),
        layer_type_description: nullableString(row['layer_type_description']) ?? '',
      }))
    );
  }

  /** GET /admin/agremo/health/layer-statuses */
  getLayerStatuses(): Observable<AgremoLayerStatusCode[]> {
    return this.request('GET', { path: 'layer-statuses' }, data => rows(data, toStatusCode));
  }

  /** GET /admin/agremo/health/analysis-types */
  getAnalysisTypes(): Observable<AgremoAnalysisTypeCodebook[]> {
    return this.request('GET', { path: 'analysis-types' }, data =>
      rows(data, row => ({ id: countOrZero(row['id']), name: nullableString(row['name']) ?? '' }))
    );
  }
}

function rows<T>(data: unknown, read: (row: Record<string, unknown>) => T): T[] {
  return Array.isArray(data) ? data.filter(isRecord).map(read) : [];
}

function toStatusCode(row: Record<string, unknown>): AgremoMapStatusCode {
  return { status_code: countOrZero(row['status_code']), status_description: nullableString(row['status_description']) ?? '' };
}

function toHealthStatus(data: unknown): AgremoHealthStatus {
  const source = isRecord(data) ? data : {};
  return {
    configured: nullableBoolean(source['configured']) ?? undefined,
    connected: source['connected'] === true,
    error: nullableString(source['error']) ?? undefined,
    checkedAt: nullableString(source['checkedAt']) ?? '',
  };
}
